import React, {FC, useState, useMemo, useCallback} from 'react'
import smoothScrollTo from 'smoothscroll'
import {useViewport, useScrollPosition} from '@rambler-id/react'

import {SideNav, SideNavItem} from 'rambler-ui'
import PersonalIcon from 'components/icons/PersonalIcon'
import SettingsIcon from 'components/icons/SettingsIcon'
import WebAuthnIcon from 'components/icons/WebAuthnIcon'
import EmailIcon from 'components/icons/EmailIcon'
import PhoneIcon from 'components/icons/PhoneIcon'
import SocialIcon from 'components/icons/SocialIcon'
import styles from './styles.css'

export interface ProfileNavigationProps {
  type: string
  emails: string[]
  webAuthnAvailableOrHasKeys: boolean
  cerberId: (target: string) => string
}

export interface ProfileSection {
  key: string
  Icon: (props: any) => JSX.Element
  title: string
}

const profileSections: ProfileSection[] = [
  {
    key: 'general',
    Icon: PersonalIcon,
    title: 'Личные данные'
  },
  {
    key: 'settings',
    Icon: SettingsIcon,
    title: 'Пароль и безопасность профиля'
  },
  {
    key: 'webauthn',
    Icon: WebAuthnIcon,
    title: 'Электронный ключ'
  },
  {
    key: 'emails',
    Icon: EmailIcon,
    title: 'Адреса электронной почты'
  },
  {
    key: 'phones',
    Icon: PhoneIcon,
    title: 'Телефонные номера'
  },
  {
    key: 'social',
    Icon: SocialIcon,
    title: 'Социальные аккаунты'
  }
]

export const ProfileNavigation: FC<ProfileNavigationProps> = ({
  type,
  emails,
  webAuthnAvailableOrHasKeys,
  cerberId = () => ''
}) => {
  const [active, setActive] = useState('general')
  const [isScrolling, setIsScrolling] = useState<boolean>(false)
  const {width} = useViewport()

  const size = useMemo(() => (width < 1280 ? 'small' : 'medium'), [width])

  const handleClick = useCallback((key) => {
    setIsScrolling(true)

    const element = document.querySelector(`[data-profile-section=${key}]`)

    smoothScrollTo(element, 500, () => setIsScrolling(false))
  }, [])

  const filteredSections: ProfileSection[] = useMemo(
    () =>
      profileSections.filter(({key}) =>
        key === 'webauthn'
          ? webAuthnAvailableOrHasKeys
          : type === 'general' || emails.length > 0 || key !== 'settings'
      ),
    [type, emails, webAuthnAvailableOrHasKeys]
  )

  const navigationItems: JSX.Element[] = useMemo(
    () =>
      filteredSections.map(({key, title, Icon}) => (
        <SideNavItem
          className={styles.item}
          key={key}
          value={key}
          icon={<Icon size={25} />}
          onClick={() => handleClick(key)}
          data-cerber-id={cerberId(key)}>
          {title}
        </SideNavItem>
      )),
    [filteredSections, handleClick, cerberId]
  )

  useScrollPosition(
    ({currScrollPosition}) => {
      if (isScrolling) {
        return
      }

      let next = active

      filteredSections.forEach(({key}) => {
        const element = document.querySelector<HTMLElement>(
          `[data-profile-section=${key}]`
        )

        if (!element) {
          return
        }

        const height = element.offsetHeight
        const top = key === 'general' ? 0 : element.offsetTop

        if (
          currScrollPosition + 100 >= top &&
          currScrollPosition + 100 <= top + height
        ) {
          next = key
        }

        if (next !== active) {
          setActive(next)
        }
      })
    },
    [active, filteredSections, isScrolling]
  )

  return (
    <SideNav
      size={size}
      value={active}
      onChange={(_, active) => setActive(active)}>
      {navigationItems}
    </SideNav>
  )
}
